<template>
  <div>
    <div class="vue-outage-sparkline-wrap">
      <b-spinner v-if="loadingSparklineData" class="spinner"></b-spinner>
      <zingchart
      :data="chartData"
      @complete="chartCompleted"
      v-if="chartVisible && chartData.series && chartData.series.length"></zingchart>
    </div>
  </div>
</template>

<script>
import * as _ from 'lodash';
import API from "@/services/api";
import moment from 'moment'
import zingchartVue from 'zingchart-vue';
import RepeatedTask from "@/services/RepeatedTask";
import constants from "../../../resources/constants.json";

const navy = '#0F2E34';
const greyTextHex = '#9EABAD';
const greyTextLight = 'rgba(158, 171, 173, .5)';
const navyBackgroundTrans95 = 'rgba(15, 46, 52, 0.95)';

export default {
  name: "OutageSparkline",
  data() {
    return {
      endDate: undefined,
      startDate: undefined,
      chartVisible: undefined,
      loadingSparklineData: undefined,
      repeatedTask: undefined,
      chartDateFormat: 'M/DD - h:mm a',
      apiDateFormat: 'YYYY-MM-DDTHH:mm',
      styleOverrideElementId: 'OutageSparkline-style-overide',
      chartData: {
          type: 'line',
          height: 125,
          width: '100%',
          legend: undefined,
          series: undefined,
          gui : {contextMenu : {empty : true}},
          theme: {
            palette: {line: [['#9EABAD' ]]},
            graph : {backgroundColor:'rgba(15, 46, 52, 0.75)'}
          },
          plotarea: {
              marginLeft:'40px',
              marginRight:'15px',
              marginTop:'10px',
              marginBottom:'30px',
              borderLeft : `3px solid ${navyBackgroundTrans95}`,
              borderBottom : `1px solid ${greyTextHex}`
          },
          plot: {
            lineColor: '#FD9303',
            highlight: true,
            shadow: 0,
            marker: {
              size: 2,
              type: 'line'
            },
            lineWidth: '2px',
            highlightState: {
              lineWidth: 3
            },
          },
          'crosshair-x': {
            plotLabel: {
              fontSize: 10,
              borderWidth: 2,
              callout: false,
              padding: '10%',
              fontColor: navy,
              borderRadius: '5px',
              fontFamily: 'Arial',
              lineStyle: 'solid',
              borderColor: greyTextHex,
              backgroundColor: '#F0F0F0',
              jsRule: 'window.outageSparklineFunctions.customCrosshairPlotLabel()'
            }
          },
          'scale-x': {
            values: [],
            transform: {
                type: 'date',
                all: '%g%a'
            },
            maxItems: 6,
            lineColor: navyBackgroundTrans95,
            item: {
                fontSize: 9,
                fontColor: greyTextHex,
                jsRule: 'window.outageSparklineFunctions.customXAxisText()'
            },
            guide: {
              lineColor: navyBackgroundTrans95
            },
            tick: {
              size: 1,
              lineWidth: 1,
              lineStyle: 'solid',
              placement: "inner",
              lineColor: navyBackgroundTrans95
            }
          },
          'scale-y': {
            lineColor: navyBackgroundTrans95,
            maxItems: 3,
            step: 500,
            item: {
              fontColor: greyTextHex
            },
            guide: {
              lineColor: greyTextLight
            },
            tick: {
              size: 1,
              lineWidth: 1,
              lineStyle: 'solid',
              placement: 'inner',
              lineColor: navyBackgroundTrans95,
            }
          }
      }
    };
  },
  async created() {
    this.setCustomJsRules();
    this.getOutagesHistory();
    this.repeatedTask = new RepeatedTask('OutageSparkline', constants.OUTAGE_SPARKLINE_DATA_RELOAD_DELAY, this.getOutagesHistory);
  },
  beforeDestroy() {
    this.repeatedTask.cancel();
    window.outageSparklineFunctions = undefined;

    const $styleOverrideElementId = document.getElementById(this.styleOverrideElementId);

    if ($styleOverrideElementId) {
      $styleOverrideElementId.remove();
    }
  },
  components: {
    zingchart: zingchartVue
  },
  methods: {
    resetChart() {
      this.chartData['scale-x'].values = [];
      this.chartData.series = undefined;
    },
    setChartDataSeries() {
      this.chartVisible = false;
      this.chartData['scale-x'].values = [];
      this.chartData.series = [];
    },
    addTimezoneOffset() {
      return (new Date().getTimezoneOffset()) * 60 * 1000;
    },
    setStartAndEndDates() {
      const baseDate = new Date();
      const now = new Date((baseDate.getTime() + this.addTimezoneOffset()));
      const past = new Date((baseDate.getTime() + this.addTimezoneOffset()) - (12 * 60 * 60 * 1000));

      this.startDate = `${moment(past).format(this.apiDateFormat)}`;
      this.endDate = `${moment(now).format(this.apiDateFormat)}`;
    },
    renderChart(incidentsSnapshot) {
      setTimeout(() => {
        this.renderChartAggregated(incidentsSnapshot);
      }, 100);
    },
    chartCompleted(evt) {
      // ensure that the "about zing charts" context menu does not appear
      const style = document.createElement('style');
      const head = document.head || document.getElementsByTagName('head')[0];
      const css = `#${evt.id}-menu.zc-menu.zc-style{ display: none!important;}`;

      head.appendChild(style);
      style.type = 'text/css';
      style.id = this.styleOverrideElementId;
      style.appendChild(document.createTextNode(css));
    },
    setCustomJsRules() {
      window.outageSparklineFunctions = {
        customXAxisText : function(evt){
          return {visible: true, text: evt.text.slice(0, -1)};
        },
        customCrosshairPlotLabel : function(evt){
          const value = evt.value;
          const past = moment(new Date(evt.scaleval)).format('MM/DD/YY - hh:mmA');
          const html = `<span class="sparkline-x-axis-crosshair">${value} outages - ${past}</span>`;
          return {visible: true, text: html};
        }
      };
    },
    getOutagesHistory() {
      this.setStartAndEndDates();
      this.setChartDataSeries();
      this.loadingSparklineData = true;
      this.incidentsSnapshot = undefined;
      this.outageHistoryApiError = undefined;

      try {
        API.GetOutagesHistory(this.startDate, this.endDate).then(jsonData => {
          this.loadingSparklineData = undefined;
          this.incidentsSnapshot = jsonData;
          this.renderChart(jsonData);
        }, err => {
          this.loadingSparklineData = undefined;
          if (err && err.response && err.response.data && err.response.data.message) {
            this.outageHistoryApiError = err.response.data.message;
          } else {
            this.outageHistoryApiError = 'There was an error loading outage history data';
          }
        })
      } catch (err) {
        console.log(`getOutagesHistory -> catch -> ${err}`);
      }
    },
    renderChartAggregated(outageHistoryData) {
      const series = this.chartData.series;
      const yAxis = 'customerCount';

      // 1 - build a map of unique outage history timestamps
      const uniqueDates = {};

      // iterate over the outageHistoryData
      _.forEach(outageHistoryData, outageHistoryRow => {
        // do we already have this timestamp?
        if (!uniqueDates[outageHistoryRow.timeStamp]){
          uniqueDates[outageHistoryRow.timeStamp] = [];
        }

        // add this timestamp to the dates map
        uniqueDates[outageHistoryRow.timeStamp].push({
          code: outageHistoryRow.utilityCode,
          name: outageHistoryRow.utilityName,
          eventCount: outageHistoryRow.eventCount,
          customerCount: outageHistoryRow.customerCount
        });
      });

      // 2 - build the x-axis chart column names

      // container for a map of unique timestamps for the chart's x-axis
      const datesForChart = {};

      // iterate over the outageHistoryData
      _.forEach(outageHistoryData, (outageHistoryRow) => {
        // skip any duplicates
        if (datesForChart[outageHistoryRow.timeStamp]) {return;}

        // mark this datesForChart property
        datesForChart[outageHistoryRow.timeStamp] = true;

        const timeStamp = new Date(moment(outageHistoryRow.timeStamp));
        this.chartData['scale-x'].values.push(new Date(timeStamp).getTime());
      });

      // 3 - build the x-axis chart column series data
      const sums = [];

      // iterate over the unique outage history timestamps
      _.forEach(uniqueDates, (utilities) => {
        // add the sum of all utilities for this outage history timestamp
        sums.push(_.sumBy(utilities, yAxis));
      });

      // set the chart data x-axis series to this summed data
      series.push({text: 'Total',  values: sums});

      // create the chart
      this.chartVisible = true;
    }
  }
};
</script>
<style lang="scss" scoped>
@import "../../../assets/css/partials/_variables.scss";

.vue-outage-sparkline-wrap{
  position: relative;
  height: 100px;

  &:hover{
    cursor: crosshair;
  }

  .spinner{
    position: absolute;
    top: 30px;
    left: 70px;
    width: 40px;
    height: 40px;
    color: $orange;
  }
}

.chart-component{
  .chart-container{
    position: relative;
  }
}
</style>